import { useState } from "react";
import "./App.css";
import NavBar from "./components/Navbar";
import CssBaseline from "@mui/material/CssBaseline";
import { Routes, Route } from "react-router-dom";
import { RelayEnvironmentProvider } from "react-relay";
import { useLocalStorage } from "@rehooks/local-storage";

import RelayEnvironment from "./RelayEnvironment";
import { ThemeProvider } from "@emotion/react";
import theme from "./theme";
import Search from "./views/Search/Search";
import GenericPage from "./views/Generic/Generic";
import GeneralTaskPage from "./views/GeneralTask/GeneralTaskPage";
import FavouritesPage from "./views/Favourites/Favourites";
import LocalStorageContext from "./contexts/LocalStorageContext";
import InversionSolutionPage from "./views/InversionSolution/InversionSolution";
import AutomationTaskPage from "./views/AutomationTask/AutomationTaskPage";
import Home from "./views/HomePage/Home";
import DisableHotkeyContext from "./contexts/DisableHotkeyContext";
import LogicTreePage from "./views/LogicTree/LogicTreePage";
import { diagnosticReportViewOptions } from "./constants/diagnosticReport";
import { SolutionDiagnosticsOption } from "./views/GeneralTask/generalTask.types";
import { parentViewsOptions } from "./constants/parentFault";
import { PlotOption, mfdPlotOptions, namedFaultsOptions } from "./constants/nameFaultsMfds";
import { FileDetailsPage } from "./views/FileDetails/FileDetails";
import {
  FAVOURITES_ROUTE,
  FILE_ROUTE,
  GENERAL_TASK_ROUTE,
  GENERIC_ROUTE,
  INVERSION_SOLUTION_ROUTE,
  LOGIC_TREE_ROUTE,
  OPENQUAKE_HAZARD_SOLUTION_ROUTE,
  TASK_ROUTE,
} from "./utils/routeUtils";
import OpenquakeHazardSolution from "./views/OpenquakeHazardSolution/OpenquakeHazardSolution";

function App() {
  const [favouriteIdArray, setFavouriteIdArray] = useLocalStorage<string[]>("favouriteIdArray", []);
  const [disableHotkey, setDisableHotkey] = useState<boolean>(false);
  const [viewOptions, setViewOptions] = useLocalStorage<SolutionDiagnosticsOption[]>("viewOptions", [
    diagnosticReportViewOptions[0],
  ]);
  const [generalViewSelections, setGeneralViewSelections] = useLocalStorage<SolutionDiagnosticsOption[]>(
    "generalViewSelections",
    [viewOptions[0]],
  );
  const [generalViews, setGeneralViews] = useLocalStorage<string[]>("generalViews", [
    diagnosticReportViewOptions[0].displayName,
  ]);
  const [parentFaultViews, setParentFaultViews] = useLocalStorage<string[]>("parentFaultViews", [
    parentViewsOptions[0].displayName,
  ]);
  const [parentFault, setParentFault] = useLocalStorage<string>("parentFault", "");
  const [namedFaultsView, setNamedFaultsView] = useLocalStorage<string>(
    "namedFaultsView",
    mfdPlotOptions[0].displayName,
  );
  const [namedFaultsLocations, setNamedFaultsLocations] = useLocalStorage<string[]>("namedFaultsLocations", [
    namedFaultsOptions[0].displayName,
  ]);
  const [mfdPlotSelection, setMfdPlotSelection] = useLocalStorage<PlotOption>("mfdPlotSelection", mfdPlotOptions[0]);

  const LocalStorageProvider = LocalStorageContext.Provider;
  const DisableHotkeyProvider = DisableHotkeyContext.Provider;

  return (
    <RelayEnvironmentProvider environment={RelayEnvironment}>
      <ThemeProvider theme={theme}>
        <LocalStorageProvider
          value={{
            favouriteIdArray,
            setFavouriteIdArray,
            viewOptions,
            setViewOptions,
            generalViewSelections,
            setGeneralViewSelections,
            generalViews,
            setGeneralViews,
            parentFaultViews,
            setParentFaultViews,
            parentFault,
            setParentFault,
            namedFaultsView,
            setNamedFaultsView,
            namedFaultsLocations,
            setNamedFaultsLocations,
            mfdPlotSelection,
            setMfdPlotSelection,
          }}
        >
          <DisableHotkeyProvider value={{ disableHotkey, setDisableHotkey }}>
            <CssBaseline>
              <NavBar />
              <Routes>
                <Route path="/Search" element={<Search />} />
                <Route path={`/${GENERAL_TASK_ROUTE}/:id/:tab?`} element={<GeneralTaskPage />} />
                <Route path={`/${GENERIC_ROUTE}/:id`} element={<GenericPage />} />
                <Route path={`/${FILE_ROUTE}/:id/:tab?`} element={<FileDetailsPage />} />
                <Route path={`/${FAVOURITES_ROUTE}`} element={<FavouritesPage />} />
                <Route path={`/${INVERSION_SOLUTION_ROUTE}/:id/:tab?`} element={<InversionSolutionPage />} />
                <Route path={`/${TASK_ROUTE}/:id`} element={<AutomationTaskPage />} />
                <Route path={`/${LOGIC_TREE_ROUTE}/:tab?`} element={<LogicTreePage />} />
                <Route path={`/${OPENQUAKE_HAZARD_SOLUTION_ROUTE}/:id/:tab?`} element={<OpenquakeHazardSolution />} />
                <Route path="/" element={<Home />} />
              </Routes>
            </CssBaseline>
          </DisableHotkeyProvider>
        </LocalStorageProvider>
      </ThemeProvider>
    </RelayEnvironmentProvider>
  );
}

export default App;
